import React, {useEffect, useState, useCallback} from 'react'
import {useParams, useNavigate, useLocation} from 'react-router-dom'
import {Helmet} from 'react-helmet'
import {useAuth} from 'app/modules/auth'
import cloudService from '../services/cloud.services'
import {CloudPageTitle} from '_metronic/layout/core/CloudPageData'
import CloudIssues from './tabs/CloudIssues'
import CloudChecks from './tabs/CloudChecks'
import { ToastContainer } from 'react-toastify'
import { parseISO, format } from 'date-fns'

interface CloudData {
  id: string
  type: string
  purpose: string
  name: string
  account_id: string
  severity: string
  open_issues: string
  ignored: string
  last_scanned: any
  created_at: any
  scan_results?: ScanResult[]
  assets?: AssetData[]
}

interface AssetData {
  id: string
  name: string
  type: string
  ecosystem: string
  package: string
  description: string
  severity: string
  source: string
  repo_url?: string
  scan_date: any
  status: string
  remediation?: string
}

interface ScanResult {
  category: string
  description: string
  message: string
  plugin: string
  regions?: string[]
  resource: string
  status: string
  title: string
}

interface CloudDetailLayoutProps {
  cloudType?: string 
}

const CloudDetailLayout: React.FC<CloudDetailLayoutProps> = ({cloudType}) => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const location = useLocation()
  const {currentUser} = useAuth()
  const [cloud, setCloud] = useState<CloudData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getCurrentTab = useCallback(() => {
    const path = location.pathname
    if (path.includes('/assets')) return 'assets'
    if (path.includes('/containers')) return 'containers'
    if (path.includes('/checks')) return 'checks'
    return 'issues'
  }, [location.pathname])

  const [activeTab, setActiveTab] = useState(getCurrentTab())

  useEffect(() => {
    setActiveTab(getCurrentTab())
  }, [getCurrentTab])

  useEffect(() => {
    if (!id || !currentUser?.tenantId) {
      setError('Invalid cloud ID or user not authenticated')
      setIsLoading(false)
      return
    }

    const fetchCloud = async () => {
      try {
        const cloudData = await cloudService.getCloudById(currentUser.tenantId, id)
        if (cloudData) {
          setCloud(cloudData as CloudData)
        } else {
          setError('Cloud not found')
        }
      } catch (err) {
        setError('Failed to fetch cloud data')
        console.error('Error fetching cloud:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCloud()
  }, [id, currentUser?.tenantId])

  const handleBackClick = () => {
    navigate('/vulnerabilities/clouds')
  }

  const handleCloudUpdate = (updatedCloud: CloudData) => {
    setCloud(updatedCloud)
  }

  const formatLastScanned = (timestamp: any) => {
    if (!timestamp) return 'Never'
    
    let date
    if (typeof timestamp === 'string') {
      date = parseISO(timestamp)
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      date = new Date(timestamp)
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    return format(date, 'd MMMM, yyyy')
  }

  const getCloudType = () => {
    return cloudType || cloud?.type || 'unknown'
  }

  const getCloudIcon = () => {
    const type = getCloudType().toLowerCase()
    switch (type) {
      case 'gcp':
        return 'Google.svg'
      case 'aws':
        return 'AWS.svg'
      case 'azure':
        return 'Azure.svg'
      default:
        return 'Cloud.svg'
    }
  }

  const renderTabContent = () => {
    if (!cloud) return null

    switch (activeTab) {
      case 'issues':
        return <CloudIssues cloud={cloud} onCloudUpdate={handleCloudUpdate} />
      // case 'assets':
      //   return <CloudAssets cloud={cloud} />
      // case 'containers':
      //   return <CloudContainers cloud={cloud} />
      case 'checks':
        return <CloudChecks cloud={cloud} onCloudUpdate={handleCloudUpdate} />
      default:
        return <CloudIssues cloud={cloud} onCloudUpdate={handleCloudUpdate} />
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{height: '50vh'}}>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error || !cloud) {
    return (
      <div className='card-body py-3'>
        <div className='text-center py-8'>
          <div className='text-danger fs-6 mb-3'>{error || 'Cloud not found'}</div>
          <button className='btn btn-primary' onClick={handleBackClick}>
            Back to Clouds
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Cloud Details - {cloud.name}</title>
      </Helmet>

      <CloudPageTitle
        icon={getCloudIcon()}
        title={cloud.name}
        desc={`${cloud.purpose} Environment • Account: ${cloud.account_id} • Last Scanned: ${formatLastScanned(cloud.last_scanned)}`}
        cloudType={getCloudType()}
        cloudId={id}
      >
        Cloud Details
      </CloudPageTitle>

      <div className='mt-6'>
        {renderTabContent()}
      </div>
      <ToastContainer />
    </>
  )
}

export default CloudDetailLayout
